import { withUkEnglish } from '../../constants/llmInstructions';

export const swotPrompt = withUkEnglish(`Output only pure, raw, unescaped JSON. Do not include any markdown, quotation marks, code blocks, or escaped characters. Do NOT stringify the JSON object — respond as if returning an API payload directly.

Analyze the company’s website, business description, sector, business model, project description, and due diligence Q&A provided.

Create a SWOT analysis specifically focused on the company’s technology, infrastructure, team, and systems, with commercial implications.

For each Weakness and Threat:
- Assign a RAG (Red, Amber, Green) status based on severity and urgency.
- Provide a clear headline, description, and practical mitigation strategy.

For each Strength and Opportunity:
- Provide a headline, description, and relevant comment or value insight.

Focus on business impact, investor risks, and post-deal scalability or integration.

Notes:
- Keep your tone clear, concise, professional, and decision-focused.
- Avoid over-technical explanations unless specifically requested.
- Always consider the investor’s perspective: is this a risk, a value lever, or a post-deal integration concern?
- Each of the four SWOT categories should include at least 3 and up to 10 items, depending on relevance and business impact.
- headline should be maximum 15 words.
- description should be maximum 25 words.
- valueInsight should be maximum 50 words.
- mitigationStrategy should be maximum 50 words.

Output structure:
{
  "swot": {
    "Strengths": [
      {
        "headline": "String",
        "description": "String - why this is a strength and how it benefits the deal",
        "valueInsight": "String - comment, recommendation, or strategic advantage"
      }
    ],
    "Weaknesses": [
      {
        "rag": "Red | Amber | Green",
        "headline": "String",
        "description": "String - explain the weakness and link it to commercial or operational risk",
        "mitigationStrategy": "String - specific recommendation to reduce or eliminate the weakness"
      }
    ],
    "Opportunities": [
      {
        "headline": "String",
        "description": "String - describe the opportunity and potential",
        "valueInsight": "String - strategic comment or recommended action"
      }
    ],
    "Threats": [
      {
        "rag": "Red | Amber | Green",
        "headline": "String",
        "description": "String - detail the threat and its potential impact",
        "mitigationStrategy": "String - actionable way to reduce or avoid the threat"
      }
    ]
  }
}
  

`);
export const opinionsPrompt = withUkEnglish(`
Output only pure, raw, unescaped JSON. Do not include any markdown, quotation marks, code blocks, or escaped characters. Do NOT stringify the JSON object — respond as if returning an API payload directly.
Your Task:
Using the provided business context, website, project description, and the Information Request (IR) Q&A:
Analyze the company’s technology, systems, security, scalability, and team capability.
Consider business model, sector-specific risks, growth potential, and deal type.
Focus on what is pivotal for an investor to know.
Answer the following four key questions from an investor’s perspective:
What is your general view of the target?
Summarize your professional opinion on the company’s overall technology and commercial position.
Highlight key strengths, weaknesses, and value drivers.
Would you invest in it?
Provide a clear YES or NO recommendation.
Justify your decision based on commercial value, risks, and growth potential.
What is the one change you would make to the company?
Identify the most impactful improvement the company should prioritize to reduce risk or unlock value.
What concerns you the most about the target?
Highlight the most significant risk or red flag that could materially affect the deal or the company’s future success.
Required Output Format
Please present the response in the following structured format:

{
opinion:[
  {
    "question": "General View of the Target",
    "answer": "The company presents a solid technical foundation with a modular, API-driven platform built for scale. Its architecture supports extensibility, while early traction and sector relevance indicate commercial potential. However, the business is still reliant on a lean technical team with limited enterprise delivery experience. Key value drivers include technical defensibility, market timing, and potential for strategic partnerships."
  },
  {
    "question": "Would You Invest in It?",
    "answer": "YES — assuming appropriate execution support and milestone-based capital deployment. The business addresses a validated market need with a scalable product, but investor oversight is required to manage delivery risk and support organizational growth. Upside exists if the company can professionalize operations and secure customer validation at scale."
  },
  {
    "question": "One Change You Would Make to the Company",
    "answer": "Formalize the product delivery and technical roadmap under experienced leadership. This will reduce execution risk, improve investor confidence, and allow the core team to focus on strategic innovation rather than firefighting delivery gaps."
  },
  {
    "question": "What Concerns You the Most About the Target",
    "answer": "The absence of experienced operational leadership and delivery governance. Without this, the company risks stalling growth, missing timelines, or accumulating technical debt — which could materially impact investor return or exit potential."
  }
]
}

Tone and Style:
Clear, concise, professional, and decision-focused.
Avoid technical jargon unless specifically requested.
Always link technical assessments to commercial and strategic outcomes.
Every answer should take maximum 75 words.
`);
export const summaryPrompt = withUkEnglish(`Output only pure, raw, unescaped JSON. Do not include any markdown, quotation marks, code blocks, or escaped characters. Do NOT stringify the JSON object — respond as if returning an API payload directly.
Your Task:
Using the provided business context, company website, project description, and Information Request Q&A:
Conduct a high-level, commercially focused assessment of the company’s technology posture, strategy, scalability, risks, and deal-critical factors.
Focus on what is vital for investors to know quickly.
Produce the following structured output:
Headline
Summarize the pivotal concern or key characteristic of the target company in one commercially significant sentence.
The headline should highlight either a critical risk, opportunity, or change in progress.
Overview
Write a concise narrative (2-4 short paragraphs) combining:
Company background and sector understanding.
Positive observations and key strengths.
Material risks or concerns.
Any high-impact changes, team dynamics, or technology evolution that might influence the investment.
Include investor-relevant nuance (e.g. significant staff departures, in-flight system migrations, reliance on specific vendors, or integration blockers).
Overall Result
Provide a summary conclusion that balances the positives and negatives.
Clearly state whether the commercial impact of the risks is acceptable or not.
End with a RAG status (Red, Amber, Green) based on your overall view of deal risk and technology maturity.

Required Output Format
Please present the response in the following structured format:

{
summary:{
headline:"Write one punchy sentence summarizing the core concern, change, or pivotal deal factor.",
overview:"Provide a concise, commercially focused narrative that combines strengths, risks, people dynamics, and technology factors. Use bullet points where appropriate to emphasize material concerns or changes.",
overallResult:"Provide a concluding statement that clearly summarizes whether the company’s technology position strengthens or weakens the investment case.",
rag:"Red | Amber | Green"
}
}

Notes:
headline should be maximum 30 words.
overview should be maximum 150 words.
overallResult should be maximum 45 words.
`);
export const highlightsPrompt = withUkEnglish(`
Output only pure, raw, unescaped JSON. Do not include any markdown, quotation marks, code blocks, or escaped characters. Do NOT stringify the JSON object — respond as if returning an API payload directly.

IMPORTANT: This is a HIGHLIGHTS analysis, NOT a summary. Do NOT return a summary structure.

Your Task:
Using the provided business context, company website, project description, and Information Request Q&A:
Analyze the company's technology, team, and cybersecurity posture.
Focus on delivering investor-relevant content, balancing strengths, concerns, and in-flight changes.
Provide clear, commercially focused observations that can help investors quickly understand the technology landscape.

Deliver content in the following three sections ONLY:
1. Team
2. Cybersecurity  
3. Technology

Required Output Format - HIGHLIGHTS STRUCTURE (NOT SUMMARY):
{
  "highlights": {
    "Team": {
      "headline": "Write one sentence summarizing the key team or leadership observation.",
      "body": "Write 2-4 sentences providing a commercially focused overview of team structure, leadership strength, people changes, and potential risks."
    },
    "Cybersecurity": { 
      "headline": "Write one sentence summarizing the key cybersecurity observation.",
      "body": "Write 2-4 sentences summarizing investment levels, security controls, monitoring, governance, and cyber risks."
    },
    "Technology": {
      "headline": "Write one sentence summarizing the key technology observation.",
      "body": "Write 2-4 sentences summarizing the technology stack, system complexity, resilience, scalability, in-flight changes, and related risks."
    }
  }
}

CRITICAL: Do NOT return a summary structure with headline/overview/overallResult/rag. Return ONLY the highlights structure above.

Notes:
- headline should be maximum 15 words.
- body should be maximum 45 words.
- This is HIGHLIGHTS analysis, NOT summary analysis.
`);
export const keyfindingsPrompt = withUkEnglish(`
Output only pure, raw, unescaped JSON. Do not include any markdown, 
quotation marks, code blocks, or escaped characters. Do NOT stringify the JSON object
 — respond as if returning an API payload directly.


Your Task:
Using the provided business context, company website, project description, and Information Request Q&A:
Analyze the company’s technology, people, systems, processes, and financial posture.
Provide a summary for each of the eight scope areas below.
Assign a RAG (Red, Amber, Green) status for each area.
For each scope area, deliver:
A punchy, commercial Headline (1-2 sentences)

Required Output Format
Please present the response in the following structured format:

{
  "keyfindings": [
    {
      "scope": "Team",
      "rag": "[Red / Amber / Green]",
      "headline": "Provide a concise, commercially focused statement about leadership structure, team dynamics, and any people-related risks or strengths. Match the word count and tone from the example provided."
    },
    {
      "scope": "Strategy & Roadmap",
      "rag": "[Red / Amber / Green]",
      "headline": "Summarize the company’s technology strategy, product roadmap, pace of change, or potential delivery concerns. Focus on post-deal impact and strategic alignment."
    },
    {
      "scope": "Technology & Architecture",
      "rag": "[Red / Amber / Green]",
      "headline": "Comment on system design, core architecture, technology stack, and known limitations. Highlight in-flight change or complexity."
    },
    {
      "scope": "Scale & Resilience",
      "rag": "[Red / Amber / Green]",
      "headline": "Comment on scalability, system performance, resilience, and any single points of failure."
    },
    {
      "scope": "Service Management & DevOps",
      "rag": "[Red / Amber / Green]",
      "headline": "Summarize how technology operations are managed, monitored, deployed, and maintained, including change management and incident handling."
    },
    {
      "scope": "Compliance & Cybersecurity",
      "rag": "[Red / Amber / Green]",
      "headline": "Comment on cybersecurity investment, governance, data protection, and regulatory alignment. Include any material gaps or maturity indicators."
    },
    {
      "scope": "Business Systems & IT",
      "rag": "[Red / Amber / Green]",
      "headline": "Summarize the back-office systems, operational IT, and any known gaps or constraints that could affect efficiency or integration."
    },
    {
      "scope": "IT Budget",
      "rag": "[Red / Amber / Green]",
      "headline": "Comment on technology spend, budget alignment, capital vs. operational split, and sufficiency of investment."
    }
  ]
}

Notes:
headline should be maximum 60 words.
`);
